<svelte:options customElement="hb-form" />

<script lang="ts">
  /**
   * Svelte Form WebComponent
   * =====================
   *
   * @contributors: Dario Caruso <dev@dariocaruso.info> (https://dariocaruso.info)
   *
   * @license: MIT License
   *
   */

  import type {
    FormSchema,
    FormSchemaDependency,
    FormSchemaEntry,
    FormValues,
    IControl,
    IRegisterComponent,
    Component,
  } from "./types/webcomponent.type";

  import pkg from "../../../package.json";

  import { groupMultipleBy } from "./libs/utils";
  import { addComponent, LanguageTranslator } from "wc-js-utils";
  const registeredComponents: IRegisterComponent = {
    row: { component: undefined, options: { row: true } },
    select: { component: "hb-input-select" },
    arrayobjects: { component: "hb-input-array-objects" },
    arraytags: { component: "hb-input-array-tags" },
    date: { component: "hb-input-date" },
    datetime: { component: "hb-input-datetime" },
    text: { component: "hb-input-text" },
    number: { component: "hb-input-number" },
    email: { component: "hb-input-email" },
    color: { component: "hb-input-color" },
    file: { component: "hb-input-file" },
    textarea: { component: "hb-input-area" },
    range: { component: "hb-input-range" },
    checkbox: {
      component: "hb-input-checkbox",
      options: { labelIsHandledByComponent: true },
    },
    radio: { component: "hb-input-radio" },
    coords: { component: "hb-input-coords" },
  };

  let {
    id = "",
    // style = "",
    schema, // Rinomina per evitare conflitti con la versione elaborata
    i18nlang,
    show_validation = "no", // Default a "no"
    hide_submit = false,
    submitted = "no",
    getvals = "no",
  }: Component = $props();

  let visibility: Record<string, boolean> = $state({});
  let valids: Record<string, boolean> = $state({});

  let allValues: FormValues = $state({});
  let dependencyMap: Record<string, FormSchemaEntry[]> = $derived.by(() => {
    if (!schema || typeof schema !== "object" || !Array.isArray(schema)) {
      return {};
    }
    const allRowEntriesAndColumnEntries = [];
    for (const el of schema) {
      if (el.type === "row" && el.params?.columns?.length) {
        allRowEntriesAndColumnEntries.push(...el.params.columns);
      } else {
        allRowEntriesAndColumnEntries.push(el);
      }
    }
    return groupMultipleBy(
      allRowEntriesAndColumnEntries.filter(
        (entry) => entry.dependencies?.length,
      ),
      (entry) => entry.dependencies!.map((dep: FormSchemaDependency) => dep.id),
    );
  });

  function flattenSchemaEntries(entries: FormSchemaEntry[]): FormSchemaEntry[] {
    const out: FormSchemaEntry[] = [];
    for (const e of entries) {
      if (e.type === "row" && e.params?.columns?.length) {
        out.push(...flattenSchemaEntries(e.params.columns));
      } else {
        out.push(e);
      }
    }
    return out;
  }

  function valueForDependency(depId: string): unknown {
    const fromAll = allValues[depId];
    if (fromAll !== undefined && fromAll !== null) return fromAll;
    if (!schema || typeof schema !== "object" || !Array.isArray(schema)) {
      return undefined;
    }
    const hit = flattenSchemaEntries(schema).find((e) => e.id === depId);
    return hit?.value;
  }

  /** Visible field values only: `allValues` wins, then schema default; excludes hidden rows (matches legacy submit shape). */
  let values: FormValues = $derived.by(() => {
    const out: FormValues = {};
    function walk(entries: FormSchemaEntry[]) {
      for (const entry of entries) {
        if (entry.type === "row" && entry.params?.columns?.length) {
          walk(entry.params.columns);
          continue;
        }
        if (visibility[entry.id]) {
          const fromAll = allValues[entry.id];
          out[entry.id] =
            fromAll !== undefined && fromAll !== null ? fromAll : entry.value;
        }
      }
    }
    if (schema && typeof schema === "object" && Array.isArray(schema)) {
      walk(schema);
    }
    return out;
  });

  let lastId: string;

  /** Avoid re-parsing and resetting the form when the parent re-passes the same schema string (e.g. funnel re-render). */
  let lastParsedSchemaString = "";

  function setVisibility(schema: FormSchema) {
    schema?.forEach?.((entry) => {
      const component = registeredComponents[entry.type];
      if (!component) {
        throw new Error(
          `FormRenderer: Tried to render an unknown component type ${entry.type}!`,
        );
      }
      if (component.options?.row) {
        setVisibility(entry.params?.columns ?? []);
      }
      visibility[entry.id] =
        visibility[entry.id] || !entry.dependencies?.length;
    });
  }
  function getControls(schema: FormSchema): IControl[] {
    return (
      schema?.map?.((entry) => {
        const component = registeredComponents[entry.type];

        if (!component) {
          throw new Error(
            `FormRenderer: Tried to render an unknown component type ${entry.type}!`,
          );
        }

        if (component.options?.row) {
          return {
            entry,
            columns: getControls(entry.params?.columns ?? []),
            options: component.options,
          };
        }

        return {
          entry,
          component: component.component,
          options: component.options || {},
        };
      }) || []
    );
  }

  let controls: IControl[] = $derived.by(() => {
    if (!schema || typeof schema !== "object" || !Array.isArray(schema)) {
      return [];
    }
    return getControls(schema);
  });

  if (
    hide_submit === true ||
    (hide_submit as unknown as string) === "yes" ||
    (hide_submit as unknown as string) === "true"
  ) {
    hide_submit = true;
  } else {
    hide_submit = false;
  }
  if (!schema) {
    console.warn("no schema provided");

    // valids = {};
    // allValues = {};
    // visibility = {};
    // controls = [];
    // values = {};
    // schema = [];
  }

  // if (
  //   !controls?.length ||
  //   !schema.length ||
  //   !controls.find((f) => f.entry.id === schema[0].id) ||
  //   lastId !== id
  // ) {
  //   console.log("schema", schema);
  //   lastId = id;
  //   valids = {};
  //   allValues = {};
  //   visibility = {};
  //   controls = [];
  //   const newValues: Record<string, string | number | boolean> = {};
  //   for (const s of schema) {
  //     if (s.type !== "row") {
  //       newValues[s.id] = s.value;
  //     } else if (s.type === "row" && s.params?.columns?.length) {
  //       for (const c of s.params.columns) {
  //         newValues[c.id] = c.value;
  //       }
  //     }
  //   }
  //   values = newValues;
  // }
  $effect(() => {
    if (typeof schema === "string") {
      try {
        const raw = schema as string;
        if (!raw.length) {
          return;
        }
        if (raw === lastParsedSchemaString) {
          return;
        }
        lastParsedSchemaString = raw;
        schema = JSON.parse(raw);
        valids = {};
        allValues = {};
        visibility = {};
        console.log("effectform");
        setVisibility(schema);
      } catch (error) {
        console.error("error with schema", schema, error);
      }
    }
  });

  /** Programmatic submit (`submitted="yes"`) must run after schema is an object; the parse effect above only handles string schema. */
  $effect(() => {
    if (submitted === "yes") {
      queueMicrotask(() => onSubmit());
    }
  });

  let is_invalid = $derived(
    !valids || !Object.entries(valids)?.length
      ? true
      : Object.entries(valids).find(
            ([id, isValid]) => !isValid && visibility && visibility[id],
          )
        ? true
        : false,
  );

  const canShow = (entry: FormSchemaEntry) => {
    for (const dep of entry.dependencies || []) {
      if (visibility && !visibility[dep.id]) {
        return false;
      }
      const depVal = valueForDependency(dep.id);
      if (
        depVal === undefined ||
        depVal === null ||
        (typeof depVal === "string" && depVal === "")
      ) {
        return false;
      }
      if (
        dep.values &&
        !dep.values.includes(depVal as string | number | boolean)
      ) {
        return false;
      }
    }

    return true;
  };

  const handleVisibility = (entry: FormSchemaEntry) => {
    if (canShow(entry)) {
      show(entry, entry.value);
    } else {
      hide(entry);
    }
  };
  const show = (entry: FormSchemaEntry, defaultValue?: unknown) => {
    visibility[entry.id] = true;

    if (entry.type !== "row") {
      if (allValues[entry.id] === undefined || allValues[entry.id] === null) {
        const initial = defaultValue !== undefined ? defaultValue : entry.value;
        if (initial !== undefined && initial !== null) {
          allValues[entry.id] = initial;
        }
      }
    }

    for (const dependentEntry of dependencyMap[entry.id] || []) {
      handleVisibility(dependentEntry);
    }
  };
  const hide = (entry: FormSchemaEntry) => {
    visibility[entry.id] = false;
    for (const d of dependencyMap[entry.id] || []) {
      hide(d);
    }
  };

  addComponent({
    repoName: "@htmlbricks/hb-input-text",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-date",
    version: pkg.version,
  });

  addComponent({
    repoName: "@htmlbricks/hb-input-email",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-checkbox",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-number",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-area",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-radio",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-select",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-file",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-color",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-range",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-datetime",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-array-objects",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-array-tags",
    version: pkg.version,
  });
  addComponent({
    repoName: "@htmlbricks/hb-input-coords",
    version: pkg.version,
  });

  let updateInterval: any = $state(undefined);
  function dispatch(name: string, detail: any) {
    if (name === "update") {
      console.log("update", detail);
      if (updateInterval) clearTimeout(updateInterval);
      updateInterval = setTimeout(() => {
        $host().dispatchEvent(new CustomEvent(name, { detail }));
      }, 300);
    } else {
      $host().dispatchEvent(new CustomEvent(name, { detail }));
    }
  }

  const onSubmit = () => {
    console.log("submitting form");
    show_validation = "yes";
    submitted = "no";
    console.log(is_invalid, JSON.stringify(valids), visibility, values);
    if (is_invalid) {
      console.warn("Form is invalid");
      dispatch("submitinvalid", {});
      return;
    }
    const cc = Object.assign({ _valid: !is_invalid }, values);

    dispatch("submit", cc);
  };
  const getVals = () => {
    if (getvals && getvals === "yes") {
      const cc = Object.assign({ _valid: !is_invalid }, values);

      dispatch("getValues", cc);
    }
    getvals = "no";
  };

  function setValByMessage(message: {
    id: string;
    value: unknown;
    valid: boolean;
  }) {
    console.info(`setValByMessage`, message);
    let update: any;
    if (allValues[message.id] !== message.value) {
      allValues[message.id] = message.value;

      if (dependencyMap[message.id]) {
        for (const dependent of dependencyMap[message.id]) {
          handleVisibility(dependent);
        }
      }
      update = Object.assign({ _valid: !is_invalid, _id: id }, values);
    }
    if (valids[message.id] !== message.valid) {
      valids[message.id] = message.valid;
      update = Object.assign({ _valid: !is_invalid, _id: id }, values);
    }
    if (update) dispatch("update", update);
  }
</script>

<div class="hb-form-root">
  {#if schema && typeof schema === "object" && schema?.length}
    {#each controls as { entry, component, options, columns } (entry.id)}
      {#if options?.row && (visibility[entry.id] || visibility[entry.id] !== false) && columns?.length ? columns.some((c: any) => visibility[c.entry.id]) : false}
        <div class="columns is-multiline">
            {#each columns as { entry, component, options } (entry.id)}
              {#if visibility[entry.id]}
                <div
                  class="column is-flex is-align-items-center"
                  style={component === "hb-input-checkbox"
                    ? "min-height:3.25rem;"
                    : ""}
                >
                  <div class="field">
                    {#if !options.labelIsHandledByComponent}
                      <label class="label" for={entry.id}
                        >{entry.label}{entry.required ? "*" : ""}</label
                      >
                    {/if}
                    {#if component === "hb-input-array-objects"}
                      <hb-input-array-objects
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                      ></hb-input-array-objects>
                    {:else if component === "hb-input-array-tags"}
                      <hb-input-array-tags
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                      ></hb-input-array-tags>
                    {:else if component === "hb-input-text"}
                      <hb-input-text
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                        onclickEnter={() => onSubmit()}
                      ></hb-input-text>
                    {:else if component === "hb-input-color"}
                      <hb-input-color
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                      ></hb-input-color>
                    {:else if component === "hb-input-range"}
                      <hb-input-range
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                      ></hb-input-range>
                    {:else if component === "hb-input-file"}
                      <hb-input-file
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {i18nlang}
                        {show_validation}
                      ></hb-input-file>
                    {:else if component === "hb-input-email"}
                      <hb-input-email
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                        onclickEnter={() => onSubmit()}
                      ></hb-input-email>
                    {:else if component === "hb-input-date"}
                      <hb-input-date
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                        onclickEnter={() => onSubmit()}
                      ></hb-input-date>
                    {:else if component === "hb-input-datetime"}
                      <hb-input-datetime
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                        onclickEnter={() => onSubmit()}
                      ></hb-input-datetime>
                    {:else if component === "hb-input-checkbox"}
                      <hb-input-checkbox
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                      ></hb-input-checkbox>
                    {:else if component === "hb-input-number"}
                      <hb-input-number
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                        onclickEnter={() => onSubmit()}
                      ></hb-input-number>
                    {:else if component === "hb-input-area"}
                      <hb-input-area
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                        onclickEnter={() => onSubmit()}
                      ></hb-input-area>
                    {:else if component === "hb-input-radio"}
                      <hb-input-radio
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                      ></hb-input-radio>
                    {:else if component === "hb-input-select"}
                      <hb-input-select
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {show_validation}
                      ></hb-input-select>
                    {:else if component === "hb-input-coords"}
                      <hb-input-coords
                        onsetVal={(d: any) => setValByMessage(d.detail)}
                        schemaentry={JSON.stringify({
                          ...entry,
                          value: allValues[entry.id] ?? entry.value,
                        })}
                        {i18nlang}
                        {show_validation}
                      ></hb-input-coords>
                    {/if}
                  </div>
                </div>
              {/if}
            {/each}
        </div>
      {:else if visibility && visibility[entry.id]}
        <div class="field">
          {#if !options?.labelIsHandledByComponent}
            <label class="label" for={entry.id}
              >{entry.label}{entry.required ? "*" : ""}</label
            >
          {/if}

          {#if component === "hb-input-text"}
            <hb-input-text
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
              onclickEnter={() => onSubmit()}
            ></hb-input-text>
          {:else if component === "hb-input-array-objects"}
            <hb-input-array-objects
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
            ></hb-input-array-objects>
          {:else if component === "hb-input-array-tags"}
            <hb-input-array-tags
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
            ></hb-input-array-tags>
          {:else if component === "hb-input-color"}
            <hb-input-color
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
            ></hb-input-color>
          {:else if component === "hb-input-range"}
            <hb-input-range
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
            ></hb-input-range>
          {:else if component === "hb-input-file"}
            <hb-input-file
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {i18nlang}
              {show_validation}
            ></hb-input-file>
          {:else if component === "hb-input-email"}
            <hb-input-email
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
              onclickEnter={() => onSubmit()}
            ></hb-input-email>
          {:else if component === "hb-input-date"}
            <hb-input-date
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
              onclickEnter={() => onSubmit()}
            ></hb-input-date>
          {:else if component === "hb-input-datetime"}
            <hb-input-datetime
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
              onclickEnter={() => onSubmit()}
            ></hb-input-datetime>
          {:else if component === "hb-input-checkbox"}
            <hb-input-checkbox
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
            ></hb-input-checkbox>
          {:else if component === "hb-input-number"}
            <hb-input-number
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
              onclickEnter={() => onSubmit()}
            ></hb-input-number>
          {:else if component === "hb-input-area"}
            <hb-input-area
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
              onclickEnter={() => onSubmit()}
            ></hb-input-area>
          {:else if component === "hb-input-radio"}
            <hb-input-radio
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
            ></hb-input-radio>
          {:else if component === "hb-input-select"}
            <hb-input-select
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {show_validation}
            ></hb-input-select>
          {:else if component === "hb-input-coords"}
            <hb-input-coords
              onsetVal={(d: any) => setValByMessage(d.detail)}
              schemaentry={JSON.stringify(
                {
                  ...entry,
                  value: allValues[entry.id] ?? entry.value,
                },
                null,
                0,
              )}
              {i18nlang}
              {show_validation}
            ></hb-input-coords>
          {/if}
        </div>
      {/if}
    {/each}
    {#if !hide_submit}
      <!-- svelte-ignore a11y_no_static_element_interactions -->
      <div
        id="button_container"
        part="button_container"
        class="is-flex is-flex-wrap-wrap is-align-items-center is-gap-2 mt-5"
      >
        <!-- svelte-ignore a11y_click_events_have_key_events -->
        <span onclick={() => onSubmit()}
          ><slot name="submit_button">
            <button part="main_button" type="button" class="button is-primary">
              <slot name="submit_label">Submit</slot>
            </button>
          </slot></span
        >
        <slot name="other_buttons" />
      </div>
    {/if}
  {/if}
</div>

<style lang="scss">
  @forward "./styles/bulma.scss";
  @forward "./styles/webcomponent.scss";
</style>
